import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// Days of the week enum
export enum DayOfWeekEnum {
  MONDAY = "MONDAY",
  TUESDAY = "TUESDAY",
  WEDNESDAY = "WEDNESDAY",
  THURSDAY = "THURSDAY",
  FRIDAY = "FRIDAY",
  SATURDAY = "SATURDAY",
  SUNDAY = "SUNDAY",
}

// Time slot schema
const timeSlotSchema = (t: TFunction) =>
  z
    .object({
      startTime: z.string().nonempty(t("common.validation.required")),
      endTime: z.string().nonempty(t("common.validation.required")),
    })
    .refine(
      (data) => {
        // Validate that end time is after start time
        const start = new Date(`2000-01-01T${data.startTime}`);
        const end = new Date(`2000-01-01T${data.endTime}`);
        return end > start;
      },
      {
        message: t("common.validation.endTimeAfterStartTime"),
        path: ["endTime"],
      }
    );

// Availability schema for a single day
const dayAvailabilitySchema = (t: TFunction) =>
  z.object({
    dayOfWeek: z.nativeEnum(DayOfWeekEnum, {
      message: t("common.validation.required"),
    }),
    isAvailable: z.boolean(),
    timeSlots: z.array(timeSlotSchema(t)).optional(),
  });

export const availabilityFormSchema = (t: TFunction) =>
  z.object({
    doctorId: z
      .string()
      .nonempty(t("common.validation.required"))
      .or(z.number()),
    healthcareFacilityId: z.number().optional(),
    effectiveDate: z
      .string()
      .nonempty(t("common.validation.required"))
      .refine((date) => {
        const effectiveDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return effectiveDate >= today;
      }, t("common.validation.effectiveDateNotPast")),
    endDate: z.string().optional().or(z.literal("")),
    availabilities: z
      .array(dayAvailabilitySchema(t))
      .min(1, t("common.validation.selectAtLeastOneDay")),
  });

export const useAvailabilityFormSchema = () => {
  const { t } = useTranslation();
  return availabilityFormSchema(t);
};

export type AvailabilityFormSchema = z.infer<
  ReturnType<typeof useAvailabilityFormSchema>
>;

// Schema for the inline add-availability form inside DoctorAvailabilitySelector
export const addAvailabilityInlineSchema = (t: TFunction) =>
  z
    .object({
      isRecurring: z.boolean(),
      date: z.string().optional(),
      startDate: z.string().optional(),
      endDate: z.string().optional(),
      daysOfWeek: z.array(z.string()),
      startTime: z.string().min(1, t("common.validation.required")),
      endTime: z.string().min(1, t("common.validation.required")),
      duration: z.string(),
    })
    .superRefine((data, ctx) => {
      if (!data.isRecurring) {
        if (!data.date) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("common.validation.required"),
            path: ["date"],
          });
        }
      } else {
        if (!data.startDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("common.validation.required"),
            path: ["startDate"],
          });
        }
        if (!data.endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("common.validation.required"),
            path: ["endDate"],
          });
        }
        if (data.startDate && data.endDate && data.startDate > data.endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("common.validation.endDateAfterStartDate"),
            path: ["endDate"],
          });
        }
        if (data.daysOfWeek.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("common.validation.selectAtLeastOneDay"),
            path: ["daysOfWeek"],
          });
        }
      }
      if (data.startTime && data.endTime && data.startTime >= data.endTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("common.validation.endTimeAfterStartTime"),
          path: ["endTime"],
        });
      }
    });

export const useAddAvailabilityInlineSchema = () => {
  const { t } = useTranslation();
  return addAvailabilityInlineSchema(t);
};

export type AddAvailabilityInlineFormData = z.infer<
  ReturnType<typeof useAddAvailabilityInlineSchema>
>;
